import { Injectable } from '@angular/core';
import { GraphsService } from '@web-services/graph/graphs.service';
import {
	LeadSourceStatisticItem,
	PieChartData,
	PieOptions
} from '@models/models';
import * as c3 from 'c3';
import * as d3 from 'd3';

@Injectable()
export class PieGraphService {
	constructor() {}

	drawTabPieChart(
		data: PieChartData,
		id: string,
		customOptions?: PieOptions
	): void {
		const { columns, colors, clearData } = data;
		const chartConfig = {
			bindto: `#${id}`,
			data: {
				columns,
				type: 'pie',
				colors
			},
			pie: {
				label: {
					format(): string {
						return null;
					}
				}
			},
			legend: {
				show: false
			},
			tooltip: {
				contents(values): string {
					return PieGraphService.addTooltip(values, clearData);
				}
			}
		};

		d3.select('.legend').remove();
		d3.select('.pie-chart-active-legend')
			.select('.legend-pie')
			.remove();

		const leadSourceIds = data.columns.map(
			(item: [string, number]): string => item[0]
		);
		const chart = c3.generate(Object.assign(chartConfig, customOptions));

		requestAnimationFrame((): void =>
			this.addPieChartLegend(
				chart,
				leadSourceIds,
				'.pie-chart-active-legend',
				customOptions,
				true
			)
		);

		requestAnimationFrame((): void => chart.flush());
	}

	drawPieChart(
		data: PieChartData,
		id: string,
		customOptions?: PieOptions
	): void {
		const { columns, colors, clearData } = data;
		const chartConfig = {
			bindto: `#${id}`,
			data: {
				columns,
				type: 'pie',
				colors
			},
			pie: {
				label: {
					format(): string {
						return null;
					}
				}
			},
			legend: {
				show: false
			},
			tooltip: {
				contents(values): string {
					return PieGraphService.addTooltip(values, clearData);
				}
			}
		};

		d3.select('.legend-pie').remove();

		const leadSourceIds = data.columns.map(
			(item: [string, number]): string => item[0]
		);
		const chart = c3.generate(Object.assign(chartConfig, customOptions));

		this.addPieChartLegend(
			chart,
			leadSourceIds,
			'.chart-pie-body',
			customOptions,
			false
		);
		PieGraphService.resizePieChart();
		chart.flush();
	}

	preparePieChartData(items: Array<LeadSourceStatisticItem>): PieChartData {
		const data = {
			columns: [],
			colors: {},
			clearData: items
		} as PieChartData;

		items.forEach((item: LeadSourceStatisticItem): void => {
			data.columns.push([item.name, item.proportionNumber]);
			data.colors[item.name] = item.colour.hex;
		});

		return data;
	}

	static resizePieChart(): void {
		const legend = d3.select('.legend-pie');
		const chart = d3.select('#pie-chart');
		const legendHeight = parseInt(legend.style('height'));
		const newHeight = 285 - legendHeight;
		chart.style('height', null);
		chart.style('height', `${newHeight}px`);
	}

	static addTooltip(values, chartData: Array<LeadSourceStatisticItem>): string {
		const [value] = values;
		const [leadSource] = chartData.filter(
			(item: LeadSourceStatisticItem): boolean => item.name === value.id
		);

		return `
				<div class="pie-tooltip">
					<p><b>${leadSource.name}</b>: ${leadSource.proportionNumber}%</p>
					
					<p><b>Count</b>: ${leadSource.count}</p>
				</div>
			`;
	}

	private addPieChartLegend = function(
		chart,
		value: Array<string>,
		containerSelector: string,
		options: PieOptions,
		columnFirst: boolean
	): void {
		d3.select(containerSelector)
			.append('div')
			.attr(
				'class',
				`legend-pie ${PieGraphService.generateLegendClasses(
					value.length,
					options.legendRowLength,
					columnFirst
				)}`
			)
			.selectAll('div')
			.data(value)
			.enter()
			.append('div')
			.attr('data-id', (id: string): string => id)
			.html((id: string): string => `<p>${id}</p>`)
			.each(function(id: string): void {
				d3.select(this)
					.insert('div', 'p')
					.attr('class', 'legend-pie-name')
					.style('background-color', chart.color(id));
			})
			.on('mouseover', (id: string): void => {
				const legendClasses = document.querySelector(`[data-id="${id}"]`)
					.classList;
				if (!legendClasses.contains('disabled')) {
					chart.focus(id);
				}
			})
			.on('mouseout', (): void => {
				chart.revert();
			});
	};

	static generateLegendClasses(
		elementsCount: number,
		maxAllowedRow: number,
		columnFirst: boolean
	): string {
		const multipleRows = elementsCount > maxAllowedRow;
		const gridType = columnFirst ? 'rows' : 'columns';
		let legend = 'grid';

		multipleRows
			? (legend += ` grid-${gridType}-${maxAllowedRow} grid-items-start`)
			: (legend += ` grid-${gridType}-${elementsCount} grid-items-center`);

		if (columnFirst) {
			legend += ' grid-fill-columns';
			legend = legend.replace('center', 'start');
		}

		return legend;
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=oom9nsliule!./web/modern/services/graph/pie-graph.service.ts